import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { Router } from '@angular/router';
import { UserService } from '../../layout/service/user.service';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

@Component({
    selector: 'app-notes-index',
    standalone: true,
    imports: [CommonModule, ButtonModule, ToastModule],
    template: `
        <div class="p-8 bg-gray-50 min-h-screen">
            <div>
                <!-- Header -->
                <div class="flex justify-between items-center mb-8">
                    <div>
                        <h1 class="text-4xl font-bold text-gray-800">Catatan</h1>
                        <p class="text-gray-500 mt-1">Catat pikiran penting Anda</p>
                    </div>
                    <button pButton label="Catatan Baru" icon="pi pi-plus" class="p-button-rounded" (click)="addNote()"></button>
                </div>

                <!-- Loading State -->
                <div *ngIf="loading" class="flex flex-col justify-center items-center h-96">
                    <i class="pi pi-spin pi-spinner text-5xl text-blue-500 mb-4"></i>
                    <p class="text-gray-500">Loading your notes...</p>
                </div>

                <!-- Empty State -->
                <div *ngIf="!loading && notes.length === 0" class="flex flex-col justify-center items-center h-96 bg-white rounded-2xl shadow-sm">
                    <i class="pi pi-book text-7xl text-gray-300 mb-4"></i>
                    <h3 class="text-2xl font-semibold text-gray-700 mb-2">No notes yet</h3>
                    <p class="text-gray-500 mb-6">Start creating your first note</p>
                    <button pButton label="Create Note" icon="pi pi-plus" (click)="addNote()"></button>
                </div>

                <!-- Notes Grid -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" *ngIf="!loading && notes.length > 0">
                    <div *ngFor="let note of notes" 
                         class="group relative bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-blue-200 cursor-pointer"
                         (click)="showNote(note)">
                        
                        <!-- Video/OSCE Banner -->
                        <div *ngIf="note.video_material_id || note.osce_id" class="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2">
                            <div class="flex items-center gap-2">
                                <i *ngIf="note.video_material_id" class="pi pi-video text-sm"></i>
                                <i *ngIf="note.osce_id" class="pi pi-book text-sm"></i>
                                <span class="text-sm font-medium">
                                    {{ note.video_material_id ? 'Video: ' + note.video_material_name : 'OSCE: ' + note.osce_title }}
                                </span>
                            </div>
                        </div>
                        
                        <!-- Color Accent Top (only for regular notes) -->
                        <div *ngIf="!note.video_material_id && !note.osce_id" class="h-2 bg-gradient-to-r from-blue-500 to-purple-500"></div>
                        
                        <!-- Card Content -->
                        <div class="p-6 flex flex-col h-full">
                            <!-- Video/OSCE Info -->
                            <div *ngIf="note.video_material_id" class="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100 cursor-pointer hover:bg-blue-100 transition" (click)="watchVideo(note); $event.stopPropagation()">
                                <div class="flex items-center gap-3 mb-2">
                                    <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                        <i class="pi pi-video text-blue-600 text-lg"></i>
                                    </div>
                                    <div class="flex-1">
                                        <h4 class="font-semibold text-gray-800 text-sm line-clamp-1">{{ note.video_material_name }}</h4>
                                        <div class="flex items-center gap-2 mt-1">
                                            <span class="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                                {{ note.video_kategori }}
                                            </span>
                                            <span class="px-2 py-0.5 rounded text-xs font-medium"
                                                  [ngClass]="getLevelClass(note.video_level)">
                                                {{ note.video_level }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex items-center gap-2 text-xs text-gray-600">
                                    <i class="pi pi-clock"></i>
                                    <span>{{ formatDuration(note.video_duration) }}</span>
                                    <span class="ml-auto text-blue-600 font-medium">Tonton Video →</span>
                                </div>
                            </div>

                            <div *ngIf="note.osce_id" class="mb-4 p-3 bg-green-50 rounded-lg border border-green-100 cursor-pointer hover:bg-green-100 transition" (click)="viewOsce(note); $event.stopPropagation()">
                                <div class="flex items-center gap-3">
                                    <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                                        <i class="pi pi-book text-green-600 text-lg"></i>
                                    </div>
                                    <div class="flex-1">
                                        <h4 class="font-semibold text-gray-800 text-sm">{{ note.osce_title }}</h4>
                                        <p class="text-xs text-gray-600 mt-1">OSCE Station</p>
                                    </div>
                                    <span class="text-green-600 font-medium text-sm">Lihat OSCE →</span>
                                </div>
                            </div>
                            
                            <!-- Note Content -->
                            <div class="mb-4 flex-grow">
                                <div [innerHTML]="note.note" class="text-gray-700 prose prose-sm max-w-none line-clamp-4"></div>
                            </div>

                            <!-- Divider -->
                            <div class="border-t border-gray-100 my-4"></div>

                            <!-- Footer - Always at bottom -->
                            <div class="flex justify-between items-center mt-auto">
                                <div class="flex gap-2">
                                    <button pButton icon="pi pi-pencil" 
                                            class="p-button-rounded p-button-text p-button-sm text-blue-600 hover:bg-blue-50" 
                                            (click)="editNote(note); $event.stopPropagation()"></button>
                                    <button pButton icon="pi pi-trash" 
                                            class="p-button-rounded p-button-text p-button-sm text-red-600 hover:bg-red-50" 
                                            (click)="deleteNote(note); $event.stopPropagation()"></button>
                                </div>
                                <div class="flex items-center gap-2 text-xs text-gray-500">
                                    <i class="pi pi-clock text-xs"></i>
                                    <span>{{ formatDate(note.created_at) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <p-toast></p-toast>
    `,
    providers: [MessageService]
})
export class NotesIndex implements OnInit {
    notes: any[] = [];
    loading = false;

    constructor(
        private userService: UserService,
        private router: Router,
        private messageService: MessageService
    ) {}

    ngOnInit() {
        this.loadNotes();
    }

    loadNotes() {
        this.loading = true;
        const userStr = localStorage.getItem('user');
        let userId = '1';
        if (userStr) {
            const user = JSON.parse(userStr);
            userId = user.ID_USER || '1';
        }
        this.userService.getNotes(userId).then(data => {
            // Assuming API returns an object with 'notes' or 'data' key, or directly an array
            this.notes = data.notes || data.data || (Array.isArray(data) ? data : []);
            this.loading = false;
        }).catch(err => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load notes' });
            this.loading = false;
        });
    }

    addNote() {
        this.router.navigate(['/app/notes/create']);
    }

    showNote(note: any) {
        this.router.navigate(['/app/notes/show', note.id]);
    }

    editNote(note: any) {
        this.router.navigate(['/app/notes/update', note.id]);
    }

    watchVideo(note: any) {
        if (note.video_material_id) {
            this.router.navigate(['/app/video_material/show', note.video_material_id]);
        }
    }

    viewOsce(note: any) {
        if (note.osce_id) {
            this.router.navigate(['/app/osce/tutor'], { queryParams: { osceId: note.osce_id } });
        }
    }

    deleteNote(note: any) {
        if (confirm('Are you sure you want to delete this note?')) {
            this.userService.deleteNotes({ id: note.id }).then(() => {
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Note deleted' });
                this.loadNotes();
            }).catch(err => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete note' });
            });
        }
    }

    formatDate(dateString: string): string {
        const date = new Date(dateString);
        date.setHours(date.getHours() + 7); // Add 7 hours for timezone
        return date.toLocaleString('id-ID', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    formatDuration(seconds: number): string {
        if (!seconds) return '';
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }

    getLevelClass(level: string): string {
        switch (level) {
            case 'Beginner': return 'bg-green-100 text-green-700';
            case 'Intermediate': return 'bg-yellow-100 text-yellow-700';
            case 'Advanced': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    }
}